import type { MaybeRefOrGetter } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import type { CompositePrimaryKey, CreateModeItemId, GlobalCollectionId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { useRoute } from '#app/composables/router';
import { isNil } from '#layers/base/public';
import { CREATE_MODE_ITEM_ID, getCanonicalCollectionRoute, getItemPrimaryKeyRouteString, useQueryApi, useWorkspaceSchemaStore, WORKSPACE_COLLECTIONS } from '#layers/data-model/public';
import { usePageIntent } from '#layers/spaces/composables/use-page-intent';
import { CONTENT_COLLECTION_DETAIL_ROUTE_NAME, CONTENT_COLLECTION_ROUTE_NAME } from '#layers/spaces/constants/content';
import { isSpaceRoute } from '#layers/spaces/utils/routes';

type GetPageWithIntent = ReturnType<typeof usePageIntent>['getPageWithIntent'];

interface ResolveCollectionRouteOptions {
	preferSpacePage?: boolean;
}

/**
 * Helper composable for retrieving the canonical listing and detail routes of a given collection.
 */
export function useCollectionRoutes(collection: MaybeRefOrGetter<ResolvedMonospaceCollection | GlobalCollectionId | null | undefined>) {
	const { findCollectionById } = useWorkspaceSchemaStore();
	const { getPageWithIntent } = usePageIntent();
	const route = useRoute();

	const computedCollection = computed(() => {
		const collectionOrId = toValue(collection);

		if (!collectionOrId) {
			return;
		}

		return typeof collectionOrId === 'string' ? findCollectionById(collectionOrId) : collectionOrId;
	});

	const collectionId = computed(() => computedCollection.value?.id);
	const supportsReadOne = useQueryApi(computedCollection).supports((api) => api.readOne() !== undefined);
	const preferSpacePage = computed(() => route.matched.some((record) => isSpaceRoute(record)));

	/**
	 * The canonical listing route for `collection`.
	 */
	const listingRoute = computed(() => computedCollection.value
		? resolveCollectionListingRoute(computedCollection.value, getPageWithIntent, { preferSpacePage: preferSpacePage.value })
		: undefined);
	const settingsRoute = computed(() => computedCollection.value ? getCollectionSettingsRoute(computedCollection.value) : undefined);

	/**
	 * Get the canonical item detail route for a given primary key. An existing item has no detail
	 * route when the collection cannot read an item by key; the create route stays available.
	 */
	function detailRoute(itemId?: CompositePrimaryKey | CreateModeItemId | null) {
		if (!computedCollection.value) {
			throw new Error(`Could not find collection with id ${collectionId.value}`);
		}

		if (!isNil(itemId) && itemId !== CREATE_MODE_ITEM_ID && !supportsReadOne.value) return;

		return resolveCollectionDetailRoute(computedCollection.value, getPageWithIntent, itemId, { preferSpacePage: preferSpacePage.value });
	}

	return {
		listingRoute,
		detailRoute,
		settingsRoute,
	};
}

export function resolveCollectionListingRoute(
	collection: ResolvedMonospaceCollection,
	getPageWithIntent?: GetPageWithIntent,
	options: ResolveCollectionRouteOptions = {},
): RouteLocationRaw | undefined {
	const collectionConfig = WORKSPACE_COLLECTIONS[collection.apiName as keyof typeof WORKSPACE_COLLECTIONS];
	const fixedRouteInfo = collectionConfig?.routes?.listing;

	if (fixedRouteInfo) {
		return fixedRouteInfo;
	}

	if (options.preferSpacePage && getPageWithIntent) {
		const page = getPageWithIntent(
			collection.id,
			'listing',
			undefined,
			undefined,
			{ includeGlobalFallback: false },
		);

		if (page) {
			return {
				name: page.id,
			};
		}
	}

	return getContentCollectionRoute(collection);
}

export function resolveCollectionDetailRoute(
	collection: ResolvedMonospaceCollection,
	getPageWithIntent?: GetPageWithIntent,
	itemId?: CompositePrimaryKey | CreateModeItemId | null,
	options: ResolveCollectionRouteOptions = {},
): RouteLocationRaw | undefined {
	let routeItemId: string | number;

	if (isNil(itemId) || itemId === CREATE_MODE_ITEM_ID) {
		routeItemId = CREATE_MODE_ITEM_ID;
	}
	else {
		routeItemId = getItemPrimaryKeyRouteString(itemId, collection);
	}

	const collectionConfig = WORKSPACE_COLLECTIONS[collection.apiName as keyof typeof WORKSPACE_COLLECTIONS];
	const fixedRouteInfo = collectionConfig?.routes?.detail;

	if (fixedRouteInfo) {
		const location = typeof fixedRouteInfo === 'function' ? fixedRouteInfo(itemId) : fixedRouteInfo;

		if (typeof location === 'object' && !('params' in location) && 'name' in location) {
			return {
				...location,
				params: {
					itemId: routeItemId,
				},
			};
		}

		return location;
	}

	if (options.preferSpacePage && getPageWithIntent) {
		const page = getPageWithIntent(
			collection.id,
			'detail',
			itemId === CREATE_MODE_ITEM_ID ? 'create' : 'edit',
			undefined,
			{ includeGlobalFallback: false },
		);

		if (page) {
			return {
				name: page.id,
				params: {
					itemId: routeItemId,
				},
			};
		}
	}

	return getContentCollectionDetailRoute(collection, routeItemId);
}

export function getContentCollectionRoute(collection: ResolvedMonospaceCollection): RouteLocationRaw | undefined {
	if (collection.isSystem) {
		return undefined;
	}

	return {
		name: CONTENT_COLLECTION_ROUTE_NAME,
		params: {
			collection: collection.apiName,
		},
	};
}

export function getContentCollectionDetailRoute(collection: ResolvedMonospaceCollection, itemId: string | number): RouteLocationRaw | undefined {
	if (collection.isSystem) {
		return undefined;
	}

	return {
		name: CONTENT_COLLECTION_DETAIL_ROUTE_NAME,
		params: {
			collection: collection.apiName,
			itemId,
		},
	};
}

export function getCollectionSettingsRoute(collection: ResolvedMonospaceCollection): RouteLocationRaw {
	return getCanonicalCollectionRoute(collection);
}
